import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, RotateCcw, Eye, Hand, Ear, Wind, Coffee, Volume2, VolumeX } from 'lucide-react';
import { createRelaxationAudio } from '../../utils/audioEngine';

const SENSE_ICONS = { sight: Eye, touch: Hand, hearing: Ear, smell: Wind, taste: Coffee };

const RelaxationPlayer = ({ config, taskTitle, onComplete, onClose }) => {
  const { steps, color } = config;
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [stepTimer, setStepTimer] = useState(0);
  const [running, setRunning] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const intervalRef = useRef(null);
  const totalElapsedRef = useRef(0);
  const audioRef = useRef(null);

  const step = steps[currentIdx];
  const SenseIcon = SENSE_ICONS[step?.sense] || Eye;
  const stepProgress = step ? Math.min(stepTimer / step.duration, 1) : 0;

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      totalElapsedRef.current += 1;
      setStepTimer(prev => prev + 1);
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  useEffect(() => {
    return () => { if (audioRef.current) audioRef.current.stop(); };
  }, []);

  const toggleAudio = () => {
    if (audioOn) {
      if (audioRef.current) audioRef.current.stop();
      audioRef.current = null; setAudioOn(false);
    } else {
      try {
        const engine = createRelaxationAudio();
        engine.start(); audioRef.current = engine; setAudioOn(true);
      } catch {}
    }
  };

  const nextStep = () => {
    if (currentIdx < steps.length - 1) { setCurrentIdx(p => p + 1); setStepTimer(0); }
    else { setRunning(false); setFinished(true); }
  };

  const handleStart = () => {
    setStarted(true);
    setRunning(true);
    if (!audioRef.current) {
      try {
        const engine = createRelaxationAudio();
        engine.start();
        audioRef.current = engine;
        setAudioOn(true);
      } catch {}
    }
  };

  const handleReset = () => {
    clearInterval(intervalRef.current);
    if (audioRef.current) { audioRef.current.stop(); audioRef.current = null; }
    setAudioOn(false); setCurrentIdx(0); setStepTimer(0); setRunning(false);
    setStarted(false); setFinished(false); totalElapsedRef.current = 0;
  };

  const handleFinish = useCallback(() => {
    if (audioRef.current) { audioRef.current.stop(); audioRef.current = null; }
    onComplete({
      started: true, duration_played: totalElapsedRef.current,
      steps_completed: currentIdx + 1, total_steps: steps.length, task_type: 'guided_relaxation',
    });
  }, [currentIdx, steps.length, onComplete]);

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-6 select-none" data-testid="relaxation-player">
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-10">
        <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 transition">
          <X className="w-5 h-5 text-white/40" />
        </button>
        <span className="text-white/30 text-xs font-medium tracking-wider uppercase">{taskTitle}</span>
        <div className="w-9" />
      </div>

      {!started && !finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="flex gap-2">
            {steps.map((s, i) => {
              const Icon = SENSE_ICONS[s.sense] || Eye;
              return (
                <div key={i} className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                  background: `${color}15`, border: `1px solid ${color}20`,
                }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
              );
            })}
          </div>
          <div className="text-center space-y-3">
            <h2 className="text-white text-2xl font-bold">{taskTitle}</h2>
            <p className="text-white/40 text-sm">{steps.length} adım — duyularınla şimdiki ana dön</p>
          </div>
          <button onClick={handleStart}
            className="px-10 py-4 rounded-2xl text-base font-bold text-white transition-all hover:scale-105 active:scale-95"
            style={{ background: `linear-gradient(135deg, ${color}, ${color}BB)`, boxShadow: `0 12px 40px ${color}40` }}
            data-testid="relaxation-start">
            Başla
          </button>
        </div>
      ) : finished ? (
        <div className="flex flex-col items-center gap-8 animate-fadeIn">
          <div className="w-28 h-28 rounded-full flex items-center justify-center" style={{ background: 'rgba(52,211,153,0.15)', boxShadow: '0 0 60px rgba(52,211,153,0.2)' }}>
            <svg className="w-14 h-14 text-emerald-400" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-white text-2xl font-bold">Grounding Tamamlandı</h2>
            <p className="text-white/40 text-sm">{steps.length} duyuyu kullanarak şimdiki ana döndün</p>
          </div>
          <div className="flex gap-3">
            <button onClick={handleReset} className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-white/50 hover:text-white/70 transition" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <RotateCcw className="w-4 h-4" /> Tekrarla
            </button>
            <button onClick={handleFinish} className="px-8 py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #34D399, #34D399BB)', boxShadow: '0 8px 30px rgba(52,211,153,0.3)' }} data-testid="relaxation-finish">
              Devam Et
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center w-full max-w-md gap-6">
          <div className="text-white/20 text-xs tracking-widest uppercase">Adım {currentIdx + 1} / {steps.length}</div>

          <div className="w-20 h-20 rounded-2xl flex items-center justify-center" style={{
            background: `${color}15`, border: `1px solid ${color}25`, boxShadow: `0 0 40px ${color}15`,
          }}>
            <SenseIcon className="w-10 h-10" style={{ color }} />
          </div>

          <h3 className="text-xl font-bold" style={{ color }}>{step.title}</h3>

          <p key={currentIdx} className="text-white/70 text-base text-center leading-relaxed max-w-xs animate-fadeIn">
            {step.text}
          </p>

          <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full transition-all duration-1000 ease-linear" style={{
              width: `${stepProgress * 100}%`, background: color,
            }} />
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => { if (currentIdx > 0) { setCurrentIdx(p => p - 1); setStepTimer(0); } }}
              disabled={currentIdx === 0}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-20"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              data-testid="relaxation-prev">
              <ChevronLeft className="w-4 h-4 text-white/60" />
            </button>
            <button onClick={toggleAudio} className="w-10 h-10 rounded-full flex items-center justify-center transition-all" style={{
              background: audioOn ? `${color}20` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${audioOn ? `${color}30` : 'rgba(255,255,255,0.06)'}`,
            }} data-testid="relaxation-audio-toggle">
              {audioOn ? <Volume2 className="w-4 h-4" style={{ color }} /> : <VolumeX className="w-4 h-4 text-white/30" />}
            </button>
            <button onClick={nextStep}
              className="flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-bold text-white transition-all hover:scale-105 active:scale-95"
              style={{ background: `${color}20`, border: `1px solid ${color}30`, boxShadow: `0 0 20px ${color}15` }}
              data-testid="relaxation-next">
              {currentIdx < steps.length - 1 ? (<>Sonraki <ChevronRight className="w-4 h-4" /></>) : 'Tamamla'}
            </button>
          </div>

          <div className="flex items-center gap-2">
            {steps.map((_, i) => (
              <div key={i} className="w-2 h-2 rounded-full transition-all duration-300" style={{
                background: i <= currentIdx ? color : 'rgba(255,255,255,0.08)',
                boxShadow: i === currentIdx ? `0 0 6px ${color}80` : 'none',
              }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelaxationPlayer;
